import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  List,
  ListItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Document, Page, pdfjs } from "react-pdf";
import Fuse from "fuse.js";
import fundoImagem from "../../assets/ImgFundo/fundo-abstratoA.jpg";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const DocumentoAvulsoView = () => {
  const apiUrl = process.env.REACT_APP_API_URL || "/api";
  const { id } = useParams();
  const location = useLocation();

  const [documento, setDocumento] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [loading, setLoading] = useState(false);
  const [bottomLoading, setBottomLoading] = useState(false);
  const [pageTexts, setPageTexts] = useState([]);
  const [autoSearchApplied, setAutoSearchApplied] = useState(false);

  const memoizedPdfUrl = useMemo(() => pdfUrl, [pdfUrl]);
  const urlQuery = useMemo(() => new URLSearchParams(location.search).get("q") || "", [location.search]);
  const urlPage = useMemo(() => {
    const raw = Number(new URLSearchParams(location.search).get("page"));
    return Number.isFinite(raw) && raw > 0 ? raw : 1;
  }, [location.search]);

  const normalizeText = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

  const normalizeForSearch = (value) =>
    normalizeText(value).replace(/[^\p{L}\p{N}\s]+/gu, " ").replace(/\s+/g, " ").trim();

  const STOP_WORDS = new Set([
    "a", "o", "e", "de", "do", "da", "dos", "das", "em", "no", "na", "nos", "nas", "um", "uma", "uns", "umas",
    "ao", "aos", "com", "sem", "por", "para", "que", "se", "sua", "seu", "suas", "seus", "os", "as",
    "del", "la", "las", "el", "los", "y", "en", "con", "sin", "una", "uno", "unos",
  ]);

  const extractRelevantTokens = (rawQuery) => {
    const normalized = normalizeForSearch(rawQuery);
    const words = normalized.split(" ").filter(Boolean);
    const filtered = words.filter((word) => word.length >= 4 && !STOP_WORDS.has(word));

    let tokens = filtered;
    if (tokens.length === 0 && words.length === 1 && words[0].length >= 3) {
      tokens = [words[0]];
    } else if (tokens.length === 0) {
      tokens = words.filter((word) => word.length >= 3);
    }

    return [...new Set(tokens)].sort((a, b) => b.length - a.length).slice(0, 8);
  };

  const onDocumentLoadSuccess = ({ numPages: pages }) => {
    setNumPages(pages);
  };

  const extractTextFromPdf = async (pdfLink) => {
    const loadingTask = pdfjs.getDocument(pdfLink);
    const doc = await loadingTask.promise;
    const pageTextsArray = [];

    for (let currentPage = 1; currentPage <= doc.numPages; currentPage += 1) {
      const page = await doc.getPage(currentPage);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item) => item.str).join(" ");
      pageTextsArray.push({ pageNumber: currentPage, text: pageText });
    }

    setPageTexts(pageTextsArray);
    return pageTextsArray;
  };

  const buildJsonIndexCandidates = (docData) => {
    const candidates = [];
    if (docData?.linkjsondocindex && typeof docData.linkjsondocindex === "string") {
      candidates.push(docData.linkjsondocindex);
    }
    if (docData?.linkpdfdoclink && typeof docData.linkpdfdoclink === "string") {
      const base = docData.linkpdfdoclink.replace(/\.pdf(\?.*)?$/i, "");
      candidates.push(`${base}_normalizado.json`);
    }
    return [...new Set(candidates.filter(Boolean))];
  };

  const tryLoadJsonIndex = async (docData) => {
    const candidates = buildJsonIndexCandidates(docData);
    for (const url of candidates) {
      try {
        const response = await fetch(url, { cache: "no-store" });
        if (!response.ok) continue;
        const payload = await response.json();
        if (!Array.isArray(payload?.pages)) continue;

        const mappedPages = payload.pages
          .map((item, idx) => ({
            pageNumber: Number(item?.page) || idx + 1,
            text: String(item?.text || ""),
          }))
          .filter((item) => item.text.trim().length > 0);

        if (mappedPages.length > 0) {
          setPageTexts(mappedPages);
          return true;
        }
      } catch (error) {
        console.error("Falha ao carregar índice JSON do documento avulso:", url, error);
      }
    }
    return false;
  };

  const computeSearchResults = (rawSearchText, pages = pageTexts) => {
    if (!rawSearchText || pages.length === 0) return [];
    const query = normalizeForSearch(rawSearchText);
    if (!query) return [];

    const queryTokens = extractRelevantTokens(rawSearchText);

    const scorePage = (pageTextRaw) => {
      const pageText = normalizeForSearch(pageTextRaw);
      if (!pageText) return 0;
      if (pageText.includes(query)) return 10000;

      let tokenMatches = 0;
      queryTokens.forEach((token) => {
        if (pageText.includes(token)) tokenMatches += 1;
      });

      return tokenMatches * 10;
    };

    let scoredResults = pages
      .map((item) => ({
        page: item.pageNumber,
        text: item.text,
        score: scorePage(item.text),
      }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || a.page - b.page);

    let results = scoredResults.map(({ page, text }) => ({ page, text }));

    if (results.length === 0) {
      const fuse = new Fuse(pages, {
        keys: ["text"],
        includeScore: true,
        threshold: 0.35,
        ignoreLocation: true,
        minMatchCharLength: 3,
      });
      results = fuse.search(rawSearchText).map((result) => ({
        page: result.item.pageNumber,
        text: result.item.text,
      }));
    }

    return results;
  };

  const handleSearch = () => {
    setBottomLoading(true);
    setLoading(true);
    if (!searchText || !pdfUrl || pageTexts.length === 0) {
      setSearchResults([]);
      setLoading(false);
      setBottomLoading(false);
      return;
    }

    const results = computeSearchResults(searchText);
    setSearchResults(results);
    setLoading(false);
    setBottomLoading(false);
    if (results.length > 0) {
      setPageNumber(results[0].page);
    }
  };

  const customTextRenderer = (textItem) => {
    if (!searchText) return textItem.str;

    const originalText = textItem.str || "";
    const normalizedTerms = extractRelevantTokens(searchText);
    const normalizedExactQuery = normalizeForSearch(searchText);
    if (normalizedTerms.length === 0) return originalText;

    const normalizedChars = [];
    const originalIndexMap = [];
    for (let i = 0; i < originalText.length; i += 1) {
      const normalizedChar = originalText[i]
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
      for (let j = 0; j < normalizedChar.length; j += 1) {
        normalizedChars.push(normalizedChar[j]);
        originalIndexMap.push(i);
      }
    }

    const normalizedText = normalizedChars.join("");
    const ranges = [];

    if (normalizedExactQuery.length >= 12 && normalizedExactQuery.split(" ").length >= 3) {
      let from = 0;
      while (from < normalizedText.length) {
        const idx = normalizedText.indexOf(normalizedExactQuery, from);
        if (idx === -1) break;
        const start = originalIndexMap[idx];
        const end = originalIndexMap[idx + normalizedExactQuery.length - 1] + 1;
        ranges.push([start, end]);
        from = idx + normalizedExactQuery.length;
      }
    }

    normalizedTerms.forEach((term) => {
      let from = 0;
      while (from < normalizedText.length) {
        const idx = normalizedText.indexOf(term, from);
        if (idx === -1) break;
        const start = originalIndexMap[idx];
        const end = originalIndexMap[idx + term.length - 1] + 1;
        ranges.push([start, end]);
        from = idx + term.length;
      }
    });

    if (ranges.length === 0) return originalText;
    ranges.sort((a, b) => a[0] - b[0]);
    const mergedRanges = [];
    ranges.forEach(([start, end]) => {
      const last = mergedRanges[mergedRanges.length - 1];
      if (!last || start > last[1]) {
        mergedRanges.push([start, end]);
      } else {
        last[1] = Math.max(last[1], end);
      }
    });

    let html = "";
    let cursor = 0;
    const escapeHtml = (value) =>
      String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    mergedRanges.forEach(([start, end]) => {
      if (start > cursor) html += escapeHtml(originalText.slice(cursor, start));
      html += `<span class="pdf-search-hit">${escapeHtml(originalText.slice(start, end))}</span>`;
      cursor = end;
    });
    if (cursor < originalText.length) html += escapeHtml(originalText.slice(cursor));
    return html;
  };

  useEffect(() => {
    const loadDocumento = async () => {
      setLoadingPdf(true);
      try {
        const response = await fetch(`${apiUrl}/documentos/${id}`);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const data = await response.json();
        setDocumento(data);
        if (data?.linkpdfdoclink) {
          setPdfUrl(data.linkpdfdoclink);
          const hasJsonIndex = await tryLoadJsonIndex(data);
          if (!hasJsonIndex) await extractTextFromPdf(data.linkpdfdoclink);
        }
      } catch (error) {
        console.error("Erro ao carregar documento avulso:", error);
      } finally {
        setLoadingPdf(false);
      }
    };

    loadDocumento();
  }, [id, apiUrl]);

  useEffect(() => {
    setAutoSearchApplied(false);
  }, [id, location.search]);

  useEffect(() => {
    if (autoSearchApplied || pageTexts.length === 0) return;

    if (urlPage > 0) setPageNumber(urlPage);
    if (urlQuery.trim()) {
      setSearchText(urlQuery);
      const results = computeSearchResults(urlQuery, pageTexts);
      setSearchResults(results);
      if (results.length > 0) {
        const preferred = results.find((result) => result.page === urlPage);
        setPageNumber(preferred ? preferred.page : results[0].page);
      }
    }

    setAutoSearchApplied(true);
  }, [autoSearchApplied, pageTexts, urlPage, urlQuery]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `url(${fundoImagem}) no-repeat center center fixed`,
        backgroundSize: "cover",
        padding: "1rem",
        width: "100%",
        maxWidth: "1500px",
        margin: "0 auto",
      }}
    >
      <style>
        {`
          .react-pdf__Page__textContent .pdf-search-hit {
            background: rgba(255, 230, 0, 0.78);
            border-radius: 2px;
            box-shadow: inset 0 0 0 1px rgba(210, 170, 0, 0.45);
          }
        `}
      </style>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography
                  variant="h2"
                  align="center"
                  gutterBottom
                  style={{
                    fontFamily: "'Lato', serif",
                    color: "#800020",
                    textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                    borderBottom: "4px solid #d4af37",
                    paddingBottom: "10px",
                    textTransform: "uppercase",
                    marginTop: "4%",
                  }}
                >
                  {documento?.titulo || "Documento"}
                </Typography>

                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ marginTop: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: "center",
                      color: "#444",
                      fontFamily: "'Lato', sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    {documento?.autor || "Documento Avulso"}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Buscar no documento"
                    placeholder="Digite sua busca..."
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                  />
                  <Button variant="contained" color="primary" onClick={handleSearch} disabled={loading}>
                    {loading ? <CircularProgress size={24} sx={{ color: "common.white" }} /> : <><SearchIcon sx={{ mr: 1 }} />Buscar</>}
                  </Button>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Button onClick={() => setPageNumber(pageNumber - 1)} variant="outlined" disabled={pageNumber <= 1}>
                    Página anterior
                  </Button>
                  <Typography variant="body1">
                    Página {pageNumber} de {numPages}
                  </Typography>
                  <Button onClick={() => setPageNumber(pageNumber + 1)} variant="outlined" disabled={pageNumber >= numPages}>
                    Próxima página
                  </Button>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={8}>
                    <Paper sx={{ p: 2, maxHeight: "80vh", overflow: "auto" }}>
                      {!loadingPdf && memoizedPdfUrl ? (
                        <Document file={memoizedPdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
                          <Page pageNumber={pageNumber} renderTextLayer customTextRenderer={customTextRenderer} width={800} />
                        </Document>
                      ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                          <CircularProgress />
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, maxHeight: "80vh", overflow: "auto" }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, textTransform: "uppercase", pb: 1 }}>
                        Resultados encontrados
                      </Typography>

                      {searchResults.length > 0 ? (
                        <List>
                          {searchResults.map((result, index) => (
                            <ListItem
                              key={`${result.page}-${index}`}
                              button
                              onClick={() => setPageNumber(result.page)}
                              sx={{ borderRadius: 1, "&:hover": { backgroundColor: "action.hover" }, mb: 1 }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 500, color: "primary.main" }}>
                                <strong>Página {result.page}</strong>
                              </Typography>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" sx={{ color: "error.main", fontStyle: "italic" }}>
                          Nenhum resultado encontrado.
                        </Typography>
                      )}

                      {bottomLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DocumentoAvulsoView;
